import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  currentLocalUser,
  getStoredToken,
  isTokenValid,
  logout as localLogout,
  LocalUser,
} from './lib/localAuth';

/**
 * Self-hosted auth context (OCI / PostgreSQL JWT) — replaces Firebase Auth.
 *
 * `currentUser` is a small shim that exposes the same surface the app already
 * used from the Firebase `User` object (`uid`, `email`, `getIdToken`,
 * `getIdTokenResult`), so existing consumers keep working without edits while
 * the token is now a locally-issued JWT.
 */
export interface ShimUser {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  emailVerified: boolean;
  getIdToken: (force?: boolean) => Promise<string | null>;
  getIdTokenResult: () => Promise<{ claims: Record<string, any> }>;
}

interface AuthContextType {
  currentUser: ShimUser | null;
  loading: boolean;
  getToken: () => Promise<string | null>;
  logout: () => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  getToken: async () => null,
  logout: () => {},
  refresh: () => {},
});

export const useAuth = () => useContext(AuthContext);

function buildShim(u: LocalUser | null): ShimUser | null {
  if (!u) return null;
  return {
    uid: u.uid,
    email: u.email,
    displayName: u.displayName || (u.email ? u.email.split('@')[0] : ''),
    role: u.role || 'Admin',
    emailVerified: true,
    getIdToken: async () => getStoredToken(),
    getIdTokenResult: async () => ({ claims: { role: u.role || 'Admin' } }),
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ShimUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (isTokenValid()) {
      setCurrentUser(buildShim(currentLocalUser()));
    } else {
      // Expired or missing — clear so route guards send the user to /login.
      localLogout();
      setCurrentUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    // Re-evaluate when another tab logs in/out.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'titan_token') refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  const getToken = async (): Promise<string | null> => getStoredToken();

  const logout = () => {
    localLogout();
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, getToken, logout, refresh }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
